import { ModelIndexProvider } from '@rhino-project/core/components/models';
import {
  useModelIndexController,
  UseModelIndexControllerOptions
} from '@rhino-project/core/hooks';
import { useEffect, useMemo } from 'react';
import { Spinner } from '@heroui/react';
import { Resources } from '@rhino-project/core';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';

export type ModelIndexSimpleProp<T extends keyof Resources> = {
  children: React.ReactNode;
  fallback?: React.ReactNode | boolean;
  syncUrl?: boolean;
} & UseModelIndexControllerOptions<T>;

export const ModelIndexSimple = <T extends keyof Resources>({
  children,
  fallback = false,
  syncUrl = false,
  ...props
}: ModelIndexSimpleProp<T>) => {
  // The URL carries an order as one comma-separated value; everything above it
  // works in terms.
  const urlSearch = useSearch({ strict: false }) as
    | (Omit<
        NonNullable<UseModelIndexControllerOptions<T>['initialState']>,
        'order'
      > & { order?: string })
    | undefined;
  const initialState = useMemo(
    () =>
      urlSearch?.order === undefined
        ? urlSearch
        : { ...urlSearch, order: urlSearch.order.split(',').filter(Boolean) },
    [urlSearch]
  ) as UseModelIndexControllerOptions<T>['initialState'] | undefined;
  const controller = useModelIndexController({
    ...(syncUrl && { initialState }),
    ...props
  });
  const {
    isEqualToDefault,
    filter,
    geospatial,
    fullFilter,
    search,
    limit,
    offset,
    order
  } = controller;
  const { isLoading } = controller;
  const navigate = useNavigate();
  const location = useLocation();

  // Fallback mirrors React 18 Suspense
  const renderFallback = useMemo(() => {
    if (!isLoading || !fallback) return children;

    if (fallback === true) return <Spinner size="sm" />;

    return fallback;
  }, [children, fallback, isLoading]);

  useEffect(() => {
    if (!syncUrl) return;

    // If the current state is the same as the default state, remove the query params from the URL but only if they are not already empty
    if (isEqualToDefault && location.search) {
      navigate({ to: location.pathname, search: {} });

      // Else update the url
    } else {
      navigate({
        to: location.pathname,
        search: {
          filter,
          geospatial,
          limit,
          offset,
          order: order.join(','),
          search
        } as Record<string, unknown>
      });
    }

    // https://github.com/facebook/react/issues/22305#issuecomment-1113508762
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps
  }, [syncUrl, filter, geospatial, fullFilter, search, limit, offset, order]);

  return (
    <ModelIndexProvider {...controller}>{renderFallback}</ModelIndexProvider>
  );
};
